import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Database, Plus, Table2, LogOut, Trash2, ChevronRight, Menu, X, Receipt, CalendarClock, Users, Star, FolderOpen, BellRing, MapPinned, Tag, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { MENU, useUi } from "@/lib/uiSettings";

const ICONS = { invoices: Receipt, service_due: CalendarClock, stickers: Tag, routes: MapPinned, reminders: BellRing, customers: FolderOpen, files: HardDrive, admin: Users };
const NAV_IDS = { invoices: "invoices-nav-link", service_due: "service-due-nav-link", stickers: "stickers-nav-link", routes: "routes-nav-link", reminders: "reminders-nav-link", customers: "customers-nav-link", files: "files-nav-link", admin: "techs-nav-link" };

function DbItem({ d, active, activeTable, onDelete, onNav, canDelete, isMain }) {
  return (
    <div className="mb-1" data-testid={`sidebar-db-${d.id}`}>
      <div className={`group flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors ${active ? "bg-secondary" : "hover:bg-secondary/60"}`}>
        <Link to={`/db/${d.id}`} onClick={onNav} className="flex-1 min-w-0 flex items-center gap-2 text-sm" data-testid={`sidebar-db-link-${d.id}`}>
          <ChevronRight size={14} className={`shrink-0 transition-transform ${active ? "rotate-90 text-primary" : "text-muted-foreground"}`} />
          <span className="truncate font-medium">{d.name}</span>
          {isMain && <Star size={11} className="shrink-0 text-amber-400 fill-amber-400" data-testid={`main-db-star-${d.id}`} />}
        </Link>
        {canDelete && <button onClick={() => onDelete(d)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400 transition-opacity"
          data-testid={`delete-db-button-${d.id}`} aria-label="Delete database"><Trash2 size={14} /></button>}
      </div>
      {active && (
        <div className="ml-5 mt-1 border-l pl-2 space-y-0.5">
          {d.tables.map((t) => (
            <Link key={t.name} to={`/db/${d.id}/${encodeURIComponent(t.name)}`} onClick={onNav}
              data-testid={`sidebar-table-${t.name.replace(/\s+/g, "-")}`}
              className={`flex items-center gap-2 rounded px-2 py-1 text-xs transition-colors ${activeTable === t.name ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}>
              <Table2 size={12} className="shrink-0" />
              <span className="truncate flex-1">{t.name}</span>
              <span className="font-mono text-[10px] opacity-70">{t.row_count}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ databases, mainId, activeDb, activeTable, onDeleted }) {
  const { user, logout, isAdmin, can } = useAuth();
  const ui = useUi();
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    const get = () => (ui.show_logo !== false ? api.get("/branding/logo", { responseType: "blob" }).then((r) => setLogo(URL.createObjectURL(r.data))).catch(() => setLogo(null)) : setLogo(null));
    get();
    window.addEventListener("logo-changed", get);
    return () => window.removeEventListener("logo-changed", get);
  }, [ui.show_logo]);
  const [toDelete, setToDelete] = useState(null);
  const [open, setOpen] = useState(false);

  const confirmDelete = async () => {
    try {
      await api.delete(`/databases/${toDelete.id}`);
      toast.success(`Deleted ${toDelete.name}`);
      onDeleted(toDelete.id);
    } catch (e) {
      toast.error(formatError(e));
    }
    setToDelete(null);
  };

  return (
    <>
      <button className="md:hidden fixed top-3 right-3 z-40 p-2 rounded-md bg-card border" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">
        {open ? <X size={18} /> : <Menu size={18} />}
      </button>
      <aside data-testid="database-sidebar"
        className={`fixed md:static z-30 inset-y-0 left-0 w-64 min-w-[256px] bg-card border-r flex flex-col transition-transform md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-4 border-b flex items-center gap-2">
          {logo ? <img src={logo} alt="" className="h-8 w-8 rounded-md object-contain bg-white" data-testid="sidebar-logo" />
            : <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center"><Database size={16} /></div>}
          <span className="font-display text-lg font-bold tracking-tight truncate" data-testid="app-name">{ui.app_name}</span>
        </div>
        <div className="p-3 space-y-2">
          {isAdmin && <Link to="/" onClick={() => setOpen(false)} data-testid="new-upload-link"
            className="flex items-center justify-center gap-2 rounded-md border border-dashed py-2 text-sm hover:border-primary hover:text-primary transition-colors">
            <Plus size={14} /> Upload database
          </Link>}
          {[...ui.menu, ...Object.keys(MENU).filter((k) => !ui.menu.some((m) => m.key === k)).map((k) => ({ key: k, label: "", hidden: false }))]
            .filter((m) => MENU[m.key] && !m.hidden && (MENU[m.key].admin ? isAdmin : (MENU[m.key].perm ? can(MENU[m.key].perm) : true))).map((m) => {
            const Icon = ICONS[m.key];
            return (
              <NavLink key={m.key} to={MENU[m.key].to} end={m.key === "invoices"} onClick={() => setOpen(false)} data-testid={NAV_IDS[m.key]}
                className={({ isActive }) => `flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary/15 text-primary" : "hover:bg-secondary"}`}>
                <Icon size={14} /> {m.label || MENU[m.key].label}
              </NavLink>
            );
          })}
        </div>
        <p className="px-4 pb-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Databases</p>
        <nav className="flex-1 overflow-y-auto px-2" data-testid="database-list">
          {databases.length === 0 && <p className="px-2 text-xs text-muted-foreground" data-testid="no-databases">Nothing imported yet.</p>}
          {databases.filter((d) => !d.parent_db_id || !databases.some((p) => p.id === d.parent_db_id)).map((d) => (
            <div key={d.id}>
              <DbItem d={d} active={d.id === activeDb} activeTable={activeTable} onDelete={setToDelete} onNav={() => setOpen(false)} canDelete={isAdmin} isMain={d.id === mainId} />
              {databases.some((c) => c.parent_db_id === d.id) && (
                <div className="ml-4 border-l pl-1.5 mb-1" data-testid={`sidebar-subs-${d.id}`}>
                  {databases.filter((c) => c.parent_db_id === d.id).map((c) => (
                    <DbItem key={c.id} d={c} active={c.id === activeDb} activeTable={activeTable} onDelete={setToDelete} onNav={() => setOpen(false)} canDelete={isAdmin} isMain={false} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="p-3 border-t flex items-center justify-between">
          <span className="text-xs text-muted-foreground truncate" data-testid="current-user-email">{user?.name || user?.email}<span className="block font-mono text-[10px] uppercase text-cyan-400" data-testid="current-user-role">{user?.role}</span></span>
          <button onClick={logout} className="text-muted-foreground hover:text-foreground" data-testid="logout-button" aria-label="Log out"><LogOut size={16} /></button>
        </div>
      </aside>
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={confirmDelete}
        title="Delete this database?" description={`"${toDelete?.name}" and all its records will be permanently removed.`} testId="delete-db" />
    </>
  );
}
